import { Col, Row } from "antd";
import { get, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineQrCode,
  MdOutlineSouthAmerica,
  MdOutlineWorkHistory,
  MdWorkOutline,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_EXERCISE_FRAME,
  JOB_LIST,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

export const showExerciseFrame = (value) =>
  [
    SOCIO_PROFESSIONAL_CATEGORIES.FARMERS,
    SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS,
    SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
  ].includes(value);

const Activity = ({
  setPercent,
  disabled,
  setDisabled,
  isFieldRequired,
  initialValues,
  bindField,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const [showSiret, setShowSiret] = useState(
    get(initialValues, "project.contact.exerciseFrame") ===
      CONTACT_EXERCISE_FRAME.INDEPENDENT,
  );

  const socioProfessionalCategoryPath =
    "project.contact.socioProfessionalCategory";
  const socioProfessionalCategoryField = bindField(
    socioProfessionalCategoryPath,
    {
      label: SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.LABEL,
      required: isFieldRequired(socioProfessionalCategoryPath),
      onChange: (value) => {
        if (
          (
            get(initialValues, "insurancePlan.config.additionalDocuments", []) ||
            []
          ).includes("proof.pensionFund")
        ) {
          setDisabled((prev) => ({
            ...prev,
            [SUBSCRIPTION_FORM_KEYS.PROOF.PENSION_FUND]:
              value === SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
          }));
        }

        if (!showExerciseFrame(value)) {
          set(
            initialValues,
            "project.contact.exerciseFrame",
            CONTACT_EXERCISE_FRAME.EMPLOYEE,
          );
        }
        setDisabled((prev) => ({
          ...prev,
          project: {
            ...prev.project,
            contact: {
              ...prev.project.contact,
              exerciseFrame: !showExerciseFrame(value),
            },
          },
        }));
      },
      onBlur: () => setPercent(initialValues),
    },
  );

  const exerciseFramePath = "project.contact.exerciseFrame";
  const exerciseFrameField = bindField(exerciseFramePath, {
    label: SUBSCRIPTION.FORM.EXERCISE_FRAMEWORK.LABEL,
    required: isFieldRequired(exerciseFramePath),
    onChange: (value) => {
      setShowSiret(value === CONTACT_EXERCISE_FRAME.INDEPENDENT);
      if (value === CONTACT_EXERCISE_FRAME.EMPLOYEE) {
        set(initialValues, "project.contact.siretNumber", null);
      }
    },
    onBlur: () => setPercent(initialValues),
  });

  const siretNumberPath = "project.contact.siretNumber";
  const siretNumberField = bindField(siretNumberPath, {
    label: SUBSCRIPTION.FORM.SIRET.LABEL,
    required: isFieldRequired(siretNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  const professionPath = "project.contact.profession";
  const professionField = bindField(professionPath, {
    label: SUBSCRIPTION.FORM.PROFESSION.LABEL,
    required: isFieldRequired(professionPath),
    disabled: get(disabled, "project.contact.profession"),
    onBlur: () => setPercent(initialValues),
  });

  const optMadelinPath = "project.contact.optMadelin";
  const optMadelinField = bindField(optMadelinPath, {
    label: SUBSCRIPTION.FORM.MADELIN_LAW.LABEL,
    required: isFieldRequired(optMadelinPath),
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={socioProfessionalCategoryField.path}
              label={socioProfessionalCategoryField.label}
              required={socioProfessionalCategoryField.required}
              disabled={socioProfessionalCategoryField.disabled}
            >
              {(field) => (
                <Select
                  options={Object.entries(SOCIO_PROFESSIONAL_CATEGORIES).map(
                    ([key, value]) => ({
                      value,
                      label:
                        SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.OPTIONS[
                          key
                        ],
                    }),
                  )}
                  label={socioProfessionalCategoryField.label}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={
                    SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.PLACEHOLDER
                  }
                  defaultValue={socioProfessionalCategoryField.defaultValue}
                  {...field}
                  onChange={socioProfessionalCategoryField.onChange}
                  onBlur={socioProfessionalCategoryField.onBlur}
                  optional={
                    !socioProfessionalCategoryField.required &&
                    SUBSCRIPTION.FORM.OPTIONAL
                  }
                />
              )}
            </FormField>
          </Col>
          {showExerciseFrame(
            get(initialValues, "project.contact.socioProfessionalCategory"),
          ) && (
            <Col lg={24} xs={24}>
              <FormField
                path={exerciseFrameField.path}
                label={exerciseFrameField.label}
                required={exerciseFrameField.required}
                disabled={exerciseFrameField.disabled}
              >
                {(field) => (
                  <Input
                    type="radio"
                    variant="group"
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    icon={<MdOutlineWorkHistory size={16} />}
                    label={exerciseFrameField.label}
                    values={[
                      {
                        value: CONTACT_EXERCISE_FRAME.EMPLOYEE,
                        label:
                          SUBSCRIPTION.FORM.EXERCISE_FRAMEWORK.OPTIONS.EMPLOYEE,
                      },
                      {
                        value: CONTACT_EXERCISE_FRAME.INDEPENDENT,
                        label:
                          SUBSCRIPTION.FORM.EXERCISE_FRAMEWORK.OPTIONS
                            .INDEPENDENT,
                      },
                    ]}
                    defaultValue={exerciseFrameField.defaultValue}
                    {...field}
                    onChange={exerciseFrameField.onChange}
                    onBlur={exerciseFrameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          )}
          {showSiret && (
            <Col lg={24} xs={24}>
              <FormField
                path={siretNumberField.path}
                label={siretNumberField.label}
                required={siretNumberField.required}
                disabled={siretNumberField.disabled}
              >
                {(field) => (
                  <Input
                    label={siretNumberField.label}
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    icon={<MdOutlineQrCode size={16} />}
                    placeholder={SUBSCRIPTION.FORM.SIRET.PLACEHOLDER}
                    type="custom"
                    trim={true}
                    defaultValue={siretNumberField.defaultValue}
                    {...field}
                    onChange={siretNumberField.onChange}
                    onBlur={siretNumberField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          )}
          <Col lg={24} xs={24}>
            <FormField
              path={professionField.path}
              label={professionField.label}
              required={professionField.required}
              disabled={professionField.disabled}
            >
              {(field) => (
                <Select
                  options={Object.entries(JOB_LIST).map(([key, value]) => ({
                    label: SUBSCRIPTION.FORM.PROFESSION.LIST[key],
                    value,
                  }))}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={SUBSCRIPTION.FORM.PROFESSION.PLACEHOLDER}
                  label={professionField.label}
                  defaultValue={professionField.defaultValue}
                  {...field}
                  onChange={professionField.onChange}
                  onBlur={professionField.onBlur}
                  optional={!professionField.required}
                />
              )}
            </FormField>
          </Col>
          {!get(disabled, "project.contact.optMadelin") && (
            <>
              <Col lg={24} xs={24}>
                <FormField
                  path={optMadelinField.path}
                  label={optMadelinField.label}
                  required={optMadelinField.required}
                  disabled={optMadelinField.disabled}
                >
                  {(field) => (
                    <Input
                      type="radio"
                      variant="group"
                      optional={SUBSCRIPTION.FORM.OPTIONAL}
                      icon={<MdOutlineSouthAmerica size={16} />}
                      label={optMadelinField.label}
                      values={[
                        {
                          value: true,
                          label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.YES,
                        },
                        {
                          value: false,
                          label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.NO,
                        },
                      ]}
                      defaultValue={optMadelinField.defaultValue}
                      {...field}
                      onChange={optMadelinField.onChange}
                      onBlur={optMadelinField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <Alert
                  showIcon
                  type="info"
                  message={SUBSCRIPTION.FORM.MADELIN_LAW.INFO}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
